import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Present.css';

function Present() {
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [medidas, setMedidas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [columnaOrden, setColumnaOrden] = useState('idPresent');

  // Paginacion (server-side)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formulario, setFormulario] = useState({
    idPresent: '',
    nomPresent: '',
    idMedida: '',
    cantidad: '1'
  });
  const [errores, setErrores] = useState({});

  // Importar Excel
  const inputImportarRef = useRef(null);
  const [importando, setImportando] = useState(false);

  const API_URL = '/api/tpresent';
  const MEDIDA_URL = '/api/medida';

  // Carga paginada desde el backend
  const cargarDatos = async (opts = {}) => {
    const _pagina    = opts.pagina    ?? pagina;
    const _busqueda  = opts.busqueda  ?? busqueda;
    const _col       = opts.col       ?? columnaOrden;
    const _asc       = opts.asc       ?? ordenAsc;
    const _porPagina = opts.porPagina ?? porPagina;
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: {
          busqueda: _busqueda,
          pagina: _pagina,
          porPagina: _porPagina,
          ordenCol: _col,
          ordenDir: _asc ? 'asc' : 'desc'
        }
      });
      const d = response.data;
      setDatosFiltrados(d.filas || []);
      setTotal(d.total || 0);
      setTotalPaginas(d.totalPaginas || 1);
      setPagina(d.pagina || 1);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    }
  };

  // Carga inicial
  useEffect(() => {
    cargarDatos({ pagina: 1 });
    cargarMedidas();
    // eslint-disable-next-line
  }, []);

  // Busqueda con debounce -> vuelve a pagina 1
  useEffect(() => {
    const t = setTimeout(() => {
      cargarDatos({ pagina: 1, busqueda });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [busqueda]);

  const cargarMedidas = async () => {
    try {
      const response = await axios.get(MEDIDA_URL);
      // Filtrar solo las medidas activas (estado = 1)
      const medidasActivas = response.data.filter(m => m.estado === 1);
      setMedidas(medidasActivas);
    } catch (error) {
      console.error('Error al cargar medidas:', error);
      alert('Error al cargar las medidas');
    }
  };

  const ordenarPor = (columna) => {
    const esAsc = columnaOrden === columna ? !ordenAsc : true;
    setColumnaOrden(columna);
    setOrdenAsc(esAsc);
    cargarDatos({ pagina: 1, col: columna, asc: esAsc });
  };

  const irAPagina = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setPagina(p);
    cargarDatos({ pagina: p });
  };

  const cambiarPorPagina = (n) => {
    setPorPagina(n);
    setPagina(1);
    cargarDatos({ pagina: 1, porPagina: n });
  };

  // Exporta TODO lo que matchea la busqueda (no solo la pagina visible)
  const exportarExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: { busqueda, pagina: 1, porPagina: 100000,
                  ordenCol: columnaOrden, ordenDir: ordenAsc ? 'asc' : 'desc' }
      });
      const datosExportar = (response.data.filas || []).map(item => ({
        'ID Presentación': item.idPresent,
        'Nombre': item.nomPresent,
        'Medida': item.medida ? item.medida.nomMedida : '',
        'Cantidad': item.cantidad
      }));

      const ws = XLSX.utils.json_to_sheet(datosExportar);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Presentaciones');
      XLSX.writeFile(wb, 'presentaciones.xlsx');
    } catch (e) {
      console.error(e);
      alert('Error al exportar');
    }
  };

  // ---------- Importar Excel ----------
  // Auto-detecta la fila de headers. Acepta columnas cuyo texto contenga
  // "present" (id_present / nom_present). Solo actualiza el nombre; los
  // nuevos ids requieren medida por ABM.
  const abrirImportar = () => inputImportarRef.current?.click();

  const onArchivoImportar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const primerSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(primerSheet, { header: 1, defval: '' });

      let headerIdx = -1;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || [];
        const c0 = String(r[0] || '').toLowerCase();
        const c1 = String(r[1] || '').toLowerCase();
        if (c0.includes('present') && c1.includes('present')) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) {
        alert('No se encontro fila de encabezados. Debe tener columnas "id_present" y "nom_present".');
        return;
      }

      const filas = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const id  = String(r[0] ?? '').trim();
        const nom = String(r[1] ?? '').trim();
        if (!id && !nom) continue;
        filas.push({ idPresent: id, nomPresent: nom });
      }

      if (filas.length === 0) {
        alert('El archivo no tiene filas de datos.');
        return;
      }

      const conf = window.confirm(
        `Se importaran ${filas.length} presentacion(es). ` +
        `Los ID existentes actualizaran solo el nombre; los IDs nuevos se ignoraran ` +
        `(medida requerida, se crean por ABM). Continuar?`
      );
      if (!conf) return;

      const { data } = await axios.post('/api/tpresent/importar', filas);
      const lineas = [
        `Import completado.`,
        `  Creados:      ${data.creados}`,
        `  Actualizados: ${data.actualizados}`,
        `  Ignorados:    ${data.ignorados}`
      ];
      if (data.errores?.length) {
        lineas.push('', 'Errores/advertencias:');
        (data.errores.slice(0, 10)).forEach(m => lineas.push('  - ' + m));
        if (data.errores.length > 10) lineas.push(`  ... y ${data.errores.length - 10} mas`);
      }
      alert(lineas.join('\n'));
      cargarDatos();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al importar: ' + err.message);
    } finally {
      setImportando(false);
      if (inputImportarRef.current) inputImportarRef.current.value = '';
    }
  };

  const abrirModalNuevo = () => {
    setFormulario({ idPresent: '', nomPresent: '', idMedida: '', cantidad: '1' });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (item) => {
    setFormulario({
      idPresent: item.idPresent,
      nomPresent: item.nomPresent,
      idMedida: item.medida ? item.medida.idMedida : '',
      cantidad: item.cantidad ? item.cantidad.toString() : '1'
    });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormulario({ idPresent: '', nomPresent: '', idMedida: '', cantidad: '1' });
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errores[name]) {
      setErrores({
        ...errores,
        [name]: ''
      });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    // Validar ID (requerido, 3 caracteres exactos, solo letras y números)
    if (!modoEdicion) {
      if (!formulario.idPresent.trim()) {
        nuevosErrores.idPresent = 'El ID es requerido';
      } else if (formulario.idPresent.trim().length !== 3) {
        nuevosErrores.idPresent = 'El ID debe tener exactamente 3 caracteres';
      } else if (!/^[a-zA-Z0-9]+$/.test(formulario.idPresent.trim())) {
        nuevosErrores.idPresent = 'El ID solo puede contener letras y números';
      }
    }

    // Validar nombre (requerido y mínimo 3 caracteres)
    if (!formulario.nomPresent.trim()) {
      nuevosErrores.nomPresent = 'El nombre es requerido';
    } else if (formulario.nomPresent.trim().length < 3) {
      nuevosErrores.nomPresent = 'El nombre debe tener al menos 3 caracteres';
    }

    // Validar medida (requerida)
    if (!formulario.idMedida) {
      nuevosErrores.idMedida = 'Debe seleccionar una medida';
    }

    // Validar cantidad (debe ser un número positivo)
    if (!formulario.cantidad || formulario.cantidad.trim() === '') {
      nuevosErrores.cantidad = 'La cantidad es requerida';
    } else {
      const cantidadNum = parseFloat(formulario.cantidad);
      if (isNaN(cantidadNum) || cantidadNum <= 0) {
        nuevosErrores.cantidad = 'La cantidad debe ser un número positivo';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulario antes de enviar
    if (!validarFormulario()) {
      return;
    }

    // Preparar datos para enviar
    const datosEnviar = {
      idPresent: formulario.idPresent.trim().toUpperCase(),
      nomPresent: formulario.nomPresent.trim(),
      idMedida: formulario.idMedida,
      cantidad: parseFloat(formulario.cantidad)
    };

    try {
      if (modoEdicion) {
        await axios.put(`${API_URL}/${formulario.idPresent}`, datosEnviar);
        alert('Presentación actualizada exitosamente');
      } else {
        await axios.post(API_URL, datosEnviar);
        alert('Presentación creada exitosamente');
      }
      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.response) {
        alert(`Error al guardar: ${error.response.data.message || 'Error del servidor'}`);
      } else if (error.request) {
        alert('Error de conexión con el servidor');
      } else {
        alert('Error al guardar la presentación');
      }
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta presentación?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        alert('Presentación eliminada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar la presentación');
      }
    }
  };

  return (
    <div className="present-container">
      <div className="header-seccion">
        <h2>Presentaciones</h2>
        <div className="acciones-header">
          <input
            type="text"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
          <button onClick={abrirModalNuevo} className="btn btn-nuevo">
            ➕ Nuevo
          </button>
          <button onClick={exportarExcel} className="btn btn-exportar">
            📊 Exportar Excel
          </button>
          <button onClick={abrirImportar} className="btn btn-exportar"
            disabled={importando}
            style={{ background: '#7B1FA2' }}
            title="Importar presentaciones desde Excel (solo actualiza nombre por id_present)">
            {importando ? '⏳ Importando...' : '📥 Importar Excel'}
          </button>
          <input type="file" accept=".xlsx,.xls" ref={inputImportarRef}
            onChange={onArchivoImportar} style={{ display: 'none' }} />
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-datos">
          <thead>
            <tr>
              <th onClick={() => ordenarPor('idPresent')} className="th-ordenable">
                ID {columnaOrden === 'idPresent' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('nomPresent')} className="th-ordenable">
                Nombre {columnaOrden === 'nomPresent' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('medida')} className="th-ordenable">
                Medida {columnaOrden === 'medida' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('cantidad')} className="th-ordenable">
                Cantidad {columnaOrden === 'cantidad' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {datosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="5" className="sin-datos">No hay datos para mostrar</td>
              </tr>
            ) : (
              datosFiltrados.map(item => (
                <tr key={item.idPresent}>
                  <td>{item.idPresent}</td>
                  <td>{item.nomPresent}</td>
                  <td>{item.medida ? item.medida.nomMedida : '-'}</td>
                  <td>{item.cantidad || 1}</td>
                  <td>
                    <button
                      onClick={() => abrirModalEditar(item)}
                      className="btn-accion btn-editar"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminar(item.idPresent)}
                      className="btn-accion btn-eliminar"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="footer-tabla" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span>Total de registros: {total}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => irAPagina(1)} disabled={pagina === 1} className="btn-paginacion">«</button>
          <button onClick={() => irAPagina(pagina - 1)} disabled={pagina === 1} className="btn-paginacion">‹</button>
          <span style={{ fontSize: '13px' }}>Pagina {pagina} de {totalPaginas}</span>
          <button onClick={() => irAPagina(pagina + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">›</button>
          <button onClick={() => irAPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">»</button>
          <select value={porPagina} onChange={(e) => cambiarPorPagina(Number(e.target.value))}
            style={{ marginLeft: '8px', padding: '4px' }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
          </select>
        </div>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modoEdicion ? 'Editar Presentación' : 'Nueva Presentación'}</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ID Presentación *</label>
                <input
                  type="text"
                  name="idPresent"
                  value={formulario.idPresent}
                  onChange={handleChange}
                  maxLength="3"
                  disabled={modoEdicion}
                  className={`form-input ${errores.idPresent ? 'form-input-error' : ''}`}
                  placeholder="3 caracteres"
                />
                {errores.idPresent && (
                  <small className="form-error">{errores.idPresent}</small>
                )}
              </div>

              <div className="form-group">
                <label>Nombre Presentación *</label>
                <input
                  type="text"
                  name="nomPresent"
                  value={formulario.nomPresent}
                  onChange={handleChange}
                  maxLength="15"
                  className={`form-input ${errores.nomPresent ? 'form-input-error' : ''}`}
                />
                {errores.nomPresent && (
                  <small className="form-error">{errores.nomPresent}</small>
                )}
              </div>

              <div className="form-group">
                <label>Medida *</label>
                <select
                  name="idMedida"
                  value={formulario.idMedida}
                  onChange={handleChange}
                  className={`form-input ${errores.idMedida ? 'form-input-error' : ''}`}
                >
                  <option value="">Seleccione una medida...</option>
                  {medidas.map(medida => (
                    <option key={medida.idMedida} value={medida.idMedida}>
                      {medida.nomMedida}
                    </option>
                  ))}
                </select>
                {errores.idMedida && (
                  <small className="form-error">{errores.idMedida}</small>
                )}
              </div>

              <div className="form-group">
                <label>Cantidad *</label>
                <input
                  type="number"
                  name="cantidad"
                  value={formulario.cantidad}
                  onChange={handleChange}
                  step="0.01"
                  min="0.01"
                  className={`form-input ${errores.cantidad ? 'form-input-error' : ''}`}
                />
                {errores.cantidad && (
                  <small className="form-error">{errores.cantidad}</small>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-guardar">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Present;
